<template>
    <div class="select">
        <div
            ref="selectContainer"
            class="select-container"
            :class="{ '_disabled': disabled }"
            :tabindex="searchable ? -1 : 0"
            role="combobox"
            @focus="showDropdown()"
            @blur="searchable ? false : hideDropdown()"
            @keyup.esc="hideDropdown"
            @keydown.self.down.prevent="onArrowDown"
            @keydown.self.up.prevent="onArrowUp"
            @keydown.enter.tab.stop.self="onEnter"
            @keypress.enter.tab.stop.self
        >
            <div
                class="select-selected"
                :class="{ '_dropdown': dropdown, '_disabled': disabled, '_error': error }"
                @mousedown.prevent="toggleDropdown"
            >
                <input
                    class="select-input"
                    :class="{ '_hide': !dropdown }"
                    ref="searchInput"
                    v-if="searchable"
                    type="text"
                    v-model="search"
                    @focus.prevent="showDropdown()"
                    @blur.prevent="hideDropdown()"
                    @keyup.esc="hideDropdown()"
                    @keydown.down.prevent="onArrowDown"
                    @keydown.up.prevent="onArrowUp"
                    @keypress.enter.prevent.stop.self="onEnter"
                    autocomplete="off"
                    spellcheck="false"
                    tabindex="0"
                >
                <template v-if="!(searchable && dropdown)">
                    <span v-if="selectedOption" v-html="selectedOption.text"></span>
                    <span v-else class="select-placeholder">{{ placeholder }}</span>
                </template>
                <div
                    class="select-icon-dropdown"
                    :class="{ '_rotate': dropdown }"
                >

                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none">
                        <path opacity="0.6" d="M1.5 1.5L5.5 5.5L9.5 1.5" stroke="#808080" stroke-width="1.5" stroke-linecap="round"></path>
                    </svg>
                </div>
            </div>
            <div ref="scrollContainer"
                 @mousedown.prevent
                 tabindex="-1"
                 class="select-dropdown"
                 :class="{ _active: dropdown }"
            >
                <div class="select-dropdown-inner">
                    <div
                        ref="options"
                        v-for="(option, index) in showedOptions"
                        :key="index"
                        v-html="option.text"
                        @click.stop="selectOption(option)"
                        @mouseenter.self="currentHighlightIndex = index"
                        class="select-dropdown-item"
                        :class="{ '_highlight': index === currentHighlightIndex, '_selected': option === selectedOption }"
                    />

                    <div v-if="!showedOptions.length">
                        Нет результатов
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vClickOutside from 'v-click-outside';

    export default {
        directives: {
            clickOutside: vClickOutside.directive,
        },
        model: {
            prop: 'modelValue',
            event: 'change',
        },
        props: {
            modelValue: {
                validator: prop => typeof prop === 'string' || typeof prop === 'number' || typeof prop === 'object' || prop === null,
                required: true,
            },
            options: {
                type: Array,
                default() {
                    return [];
                },
            },
            placeholder: {
                type: String,
                default: 'Не выбрано',
            },
            searchable: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            error: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                dropdown: false,
                search: '',
                currentHighlightIndex: 0,
            }
        },
        computed: {
            selectedOption() {
                return this.options.find((option) => option.value === this.modelValue);
            },
            showedOptions() {
                return this.options.filter((option) => option.text.toLowerCase().includes(this.search.toLowerCase()))
            }
        },
        watch: {
            showedOptions() {
                this.currentHighlightIndex = 0;
            }
        },
        methods: {
            showDropdown() {
                if (this.disabled) return;

                this.dropdown = true;

                if (this.searchable) {
                    this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
                } else {
                    this.$refs.selectContainer.focus();
                }

                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus();
                }
            },
            hideDropdown() {
                this.$nextTick(() => {
                    this.dropdown = false;
                    this.search = '';

                    if (this.searchable) {
                        this.$refs.searchInput && this.$refs.searchInput.blur();
                    } else {
                        this.$refs.selectContainer.blur();
                    }
                });
            },
            toggleDropdown() {
                this.dropdown ? this.hideDropdown() : this.showDropdown();
            },
            selectOption(option) {
                if (this.disabled) return;

                this.hideDropdown();
                this.$emit('change', option.value);
            },
            onArrowDown() {
                if (this.currentHighlightIndex < this.showedOptions.length - 1) {
                    this.currentHighlightIndex = this.currentHighlightIndex + 1;
                    this.fixScrolling()
                }
            },
            onArrowUp() {
                if (this.currentHighlightIndex > 0) {
                    this.currentHighlightIndex = this.currentHighlightIndex - 1;
                    this.fixScrolling()
                }
            },
            onEnter() {
                this.selectOption(this.showedOptions[this.currentHighlightIndex]);
            },
            getOptionHeight() {
                return this.$refs.options[this.currentHighlightIndex].clientHeight || 0;
            },
            fixScrolling() {
                this.$refs.scrollContainer.scrollTop = this.getOptionHeight() * this.currentHighlightIndex;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .select {
        font-size: 16px;
        line-height: 19px;

        @media screen and (max-width: 767px) {
            font-size: 12px;
            line-height: 14px;
        }

        &-container {
            position: relative;
            outline: none;

            &._disabled {
                pointer-events: none;
            }
        }

        &-input {
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            min-height: 20px;
            line-height: 20px;
            border: none;
            border-radius: 5px;
            background: #fff;
            width: 100%;
            transition: border .1s ease;
            box-sizing: border-box;
            margin-bottom: 8px;
            vertical-align: top;
            outline: none;
            padding-left: 14px;
            padding-right: 35px;

            &::-ms-reveal {
                display: none;
            }

            &::-ms-clear {
                display: none;
            }

            &._hide {
                width: 0px;
                position: absolute;
                padding: 0px;
            }
        }

        &-placeholder {
            opacity: 0.5;
        }

        &-selected {
            cursor: pointer;
            position: relative;
            height: 40px;
            background-color: #fff;
            padding-right: 35px;
            padding-left: 14px;
            line-height: 40px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            border-radius: 10px;

            @media screen and (max-width: 767px) {
                height: 30px;
                line-height: 30px;
                border-radius: 7.5px;
            }

            &::after {
                pointer-events: none;
                display: block;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                border: 1px solid #e6e6e6;
                content: '';
                border-radius: inherit;
            }

            &:hover {
                &::after {}
            }

            &._dropdown {
                border-radius: 10px 10px 0 0;

                &::after {}
            }

            &._error:not(._disabled) {
                &::after {
                    border-color: red;
                }
            }

            &._disabled {
                pointer-events: none;
                opacity: 0.48;
            }
        }

        &-icon {
            &-dropdown {
                position: absolute;
                transform: translateY(-50%);
                top: 50%;
                right: 13px;
                width: 12px;

                &._rotate {
                    transform: translateY(-50%) rotate(-180deg);
                }

                &::before {
                    position: absolute;
                    top: 0;
                    right: -100%;
                    bottom: 0;
                    left: -100%;
                    content: '';
                }
            }
        }

        &-dropdown {
            position: absolute;
            right: 0;
            left: 0;
            border: 1px solid #e6e6e6;
            border-top: none;
            background-color: #fff;
            top: 100%;
            border-radius: 0 0 10px 10px;
            z-index: 1;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            box-shadow: 10px 14px 50px rgba(229,233,246, .8);
            overflow: hidden;

            &._active {
                pointer-events: all;
                opacity: 1;
                visibility: visible;
            }

            &-inner {
                overflow-y: auto;
                max-height: 340px;
            }

            &-item {
                cursor: pointer;
                padding: 8px 14px;

                &:first-child {}

                &:last-child {}

                &._highlight {
                    background-color: #00ABAA;
                    color: #fff;
                }

                &._selected {
                    background-color: #00ABAA;
                    color: #fff;
                }
            }
        }
    }
</style>
